import { makeAutoObservable } from 'mobx';
import ILoadingProps, { ILoadingPropsDispatcher } from '../interfaces/ILoadingProps';
import ITenantInMarket, { ITenantInMarketState } from '../interfaces/ITenantInMarket';
import Shape from '../interfaces/Shape';
import TenantsInMarketApiService from '../services/tenantsInMarketApiService';

export default class TenantInMarketStore {
    tenantsInMarket: ITenantInMarket[] = [];
    isTenantsInMarketLoading: ILoadingProps = {};

    constructor(
        private tenantInMarketApiService: TenantsInMarketApiService,
    ) {
        makeAutoObservable(this);
    }

    setTenantsInMarket(value: ITenantInMarket[]) {
        this.tenantsInMarket = value;
    }

    setIsTenantsInMarketLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isTenantsInMarketLoading.fetching = value,
            creating: (value: boolean) => this.isTenantsInMarketLoading.creating = value,
            updating: (value: boolean) => this.isTenantsInMarketLoading.updating = value,
            deleting: (value: boolean) => this.isTenantsInMarketLoading.deleting = value,
        };
    }

    initialize() {
        this.setIsTenantsInMarketLoading().fetching(true);
        this.tenantInMarketApiService.getTenantsInMarket().then(tenantsInMarket => {
            this.setTenantsInMarket(tenantsInMarket);
        }).finally(() => {
            this.setIsTenantsInMarketLoading().fetching(false);
        });
    }

    async createTenantInMarket(tenantInMarketState: ITenantInMarketState, pipedriveCompanyId: number | null, pipedriveBrokerageId: number | null, shape: Shape, geography: string): Promise<number> {
        this.setIsTenantsInMarketLoading().creating(true);
        try {
            const response = await this.tenantInMarketApiService.createTenantInMarket(tenantInMarketState, pipedriveCompanyId, pipedriveBrokerageId, shape, geography);
            this.setTenantsInMarket([
                ...this.tenantsInMarket,
                {
                    ...tenantInMarketState,
                    landAreaMin: tenantInMarketState.landAreaMin!,
                    landAreaMax: tenantInMarketState.landAreaMax!,
                    buildingAreaMin: tenantInMarketState.buildingAreaMin!,
                    buildingAreaMax: tenantInMarketState.buildingAreaMax!,
                    assignedToPipedriveUserId: tenantInMarketState.assignedToPipedriveUserId!,
                    shape: shape,
                    id: response.id,
                    pipedriveCompanyId: pipedriveCompanyId,
                    pipedriveBrokerageId: pipedriveBrokerageId,
                    modifiedByUser: response.modifiedByUser,
                    modifiedDate: response.modifiedDate,
                    createdAt: response.modifiedDate,
                },
            ]);

            return response.id;
        } finally {
            this.setIsTenantsInMarketLoading().creating(false);
        }
    }

    async updateTenantInMarket(tenantInMarket: ITenantInMarket) {
        this.setIsTenantsInMarketLoading().updating(true);
        try {
            const response = await this.tenantInMarketApiService.updateTenantInMarket(tenantInMarket);
            tenantInMarket.modifiedByUser = response.modifiedByUser;
            tenantInMarket.modifiedDate = response.modifiedDate;
            tenantInMarket.createdAt = response.modifiedDate;

            this.setTenantsInMarket([
                ...this.tenantsInMarket.filter(tim => tim.id !== tenantInMarket.id),
                tenantInMarket,
            ]);
        } finally {
            this.setIsTenantsInMarketLoading().updating(false);
        }
    }

    async deleteTenantInMarket(id: number) {
        this.setIsTenantsInMarketLoading().deleting(true);
        try {
            await this.tenantInMarketApiService.deleteTenantInMarket(id);

            this.setTenantsInMarket([
                ...this.tenantsInMarket.filter(tim => tim.id !== id),
            ]);
        } finally {
            this.setIsTenantsInMarketLoading().deleting(false);
        }
    }

    clear() {
        this.setTenantsInMarket([]);
        this.isTenantsInMarketLoading = {};
    }
}